import { jwtDecode } from 'jwt-decode';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiError, refreshTokenAPI } from '.';
import { PUBLIC_API_ENDPOINT } from '../constants/platform';
import { errorResponse } from '../utils';
import { LocalStorageService } from '../configuration';
import { routePaths } from '../routes/routes-path';
import { moveTotpToHeader } from '../utils/admin-stepup';
import { notifyViewerForbidden } from '../utils/viewer-notice';
import { isAuthEndpoint } from '../utils/admin-login-error';

// QA S-24: mutex refresh token — nhiều request 401 song song (và ensureFreshAccessToken của SignalR)
// dùng CHUNG một lần refresh đang bay, không bao giờ refresh song song.
let refreshInFlight: Promise<string> | null = null;

const API = axios.create({
  baseURL: `${PUBLIC_API_ENDPOINT}`,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
  // Token-binding: luôn gửi/nhận cookie httpOnly (cab_rt, fingerprint) dù API khác origin với web.
  // Trang công khai/OBS không có vé vẫn chạy bình thường (cookie chỉ được đính kèm nếu có).
  withCredentials: true,
});

// Tách phiên ADMIN khỏi phiên web chính: đang ở trang /admin thì dùng token admin riêng
// ('cmsAccessToken'), ngược lại dùng token user ('accessToken'). Nhờ vậy đăng nhập admin KHÔNG
// làm web chính bị đăng nhập theo tài khoản đó.
const isAdminContext = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

/**
 * Trang CÔNG KHAI: xem không cần đăng nhập.
 *
 * Quan trọng với overlay livestream: OBS mở bằng trình duyệt sạch, KHÔNG có token.
 * Trước đây chỉ cần 1 lời gọi nền bị 401 (vd datings/*) là interceptor đá cả trang
 * về /account/login -> link OBS của streamer, /reels, /old đều không dùng được.
 * Ở các trang này, 401 chỉ làm hỏng đúng lời gọi đó, KHÔNG được cướp cả trang.
 */
const PUBLIC_PAGE_PREFIXES = [
  '/donate-screen/',
  '/donate-goal-screen/',
  '/spin-wheel-screen/',
  '/donate-poll-screen/',
  '/donate-challenge-screen/',
  '/music-screen/',
  '/effect-screen/',
  '/member-screen/',
  '/hoi-dap-screen/',
  '/subathon-screen/',
  '/top-donate-screen/',
  '/ad-screen/',
  '/su-kien/',
  '/go/',
  '/p/',
  '/reels',
  '/old',
  '/c/',
  '/vinh-danh',
  '/campaigns',
];
const isPublicPage = () =>
  typeof window !== 'undefined' &&
  PUBLIC_PAGE_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix)
  );
// Lấy vé (không kèm 'Bearer ') từ header Authorization của request.
const bearerOf = (headers: unknown): string | undefined => {
  const h = headers as { Authorization?: unknown; get?: (n: string) => unknown } | undefined;
  const raw = h?.Authorization ?? h?.get?.('Authorization');
  return typeof raw === 'string' ? raw.replace(/^Bearer\s+/i, '') : undefined;
};
const tokenKey = () => (isAdminContext() ? 'cmsAccessToken' : 'accessToken');

API.interceptors.request.use(
  (config) => {
    if (config.headers) {
      const accessToken = LocalStorageService.getItem(tokenKey());
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else {
        // Không có token cho ngữ cảnh này → gỡ header để không lỡ dùng token mặc định của web chính.
        delete config.headers.Authorization;
      }
    }
    return moveTotpToHeader(config);
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

API.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  async (error: AxiosError): Promise<AxiosResponse | Promise<AxiosError>> => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    const { statusCode } = errorResponse(error);
    // Chỉ coi là 401-của-ADMIN khi REQUEST gọi tới endpoint admin (theo URL) — KHÔNG theo trang hiện tại,
    // nếu không thì lời gọi web chính (/me, /datings...) chạy trên trang /admin bị 401 sẽ kích redirect →
    // reload → gọi lại → LOOP vô tận. Và không redirect khi đã ở /admin/login (chống loop lần nữa).
    const reqUrl = error.config?.url || '';
    const isAdminCall =
      reqUrl.includes('/admin') ||
      reqUrl.includes('/cms/') ||
      reqUrl.includes('/reportcomments');
    if (
      error.response?.status === 401 &&
      isAdminCall &&
      window.location.pathname !== '/admin/login'
    ) {
      localStorage.removeItem('cmsAccessToken');
      window.location.href = '/admin/login';
      return Promise.reject(error);
    }
    // Tài khoản chỉ xem bị cổng API chặn thao tác (403): báo, KHÔNG đăng xuất / chuyển trang.
    if (isAdminContext() && notifyViewerForbidden(error.response?.status)) {
      return Promise.reject(error);
    }
    // Chính các endpoint xác thực bị 401 (sai mật khẩu, khoá tạm...) thì trả lỗi gốc, không refresh.
    if (isAuthEndpoint(reqUrl)) {
      return Promise.reject(error);
    }
    if (statusCode === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      // Vé request này đã dùng (để biết tab khác có vừa refresh xong không).
      const usedToken = bearerOf(originalRequest?.headers);
      try {
        await refreshAccessToken();
        return API(originalRequest);
      } catch (refreshErr) {
        // Tab khác vừa refresh xong (vé dùng chung localStorage đã khác vé request này dùng) →
        // thử lại với vé mới, KHÔNG xoá vé và KHÔNG đá về login.
        const currentToken = LocalStorageService.getItem(tokenKey());
        if (currentToken && currentToken !== usedToken) {
          return API(originalRequest);
        }
        // Chỉ khi máy chủ từ chối refresh (400/401) mới coi phiên đã chết. Lỗi mạng, timeout, 5xx
        // (vd 502 lúc deploy khởi động lại) → giữ vé, không chuyển trang, request sau còn thử lại được.
        if (isRefreshRejected(refreshErr)) {
          LocalStorageService.removeItem('accessToken');
          LocalStorageService.removeItem('refreshToken');
          if (
            typeof window !== 'undefined' &&
            !window.location.pathname.startsWith('/admin') &&
            !isPublicPage() &&
            window.location.pathname !== routePaths.login
          ) {
            window.location.href = routePaths.login;
          }
        }
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

// Máy chủ trả 400/401 cho chính lời gọi refresh = cookie refresh hết hạn/bị thu hồi → phiên đã chết.
const isRefreshRejected = (e: unknown) => {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 400 || status === 401;
};

// Refresh dùng chung: gọi đồng thời chỉ bay MỘT request, lưu vé mới vào localStorage và trả về vé.
const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    // Refresh chỉ dựa vào cookie httpOnly (cab_rt) — BE bỏ qua refresh token trong body.
    const fingerprintHashLocal =
      LocalStorageService.getItem('fingerprintHash') || undefined;
    const {
      data: { accessToken, fingerprintHash },
    } = await refreshTokenAPI({ fingerprintHash: fingerprintHashLocal });

    if (fingerprintHash)
      LocalStorageService.setItem('fingerprintHash', fingerprintHash);
    LocalStorageService.setItem('accessToken', accessToken);
    // Migration: BE đã set cookie cab_rt → bỏ refresh token khỏi localStorage.
    LocalStorageService.removeItem('refreshToken');
    return accessToken as string;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

/**
 * Cho SignalR: vé user sống ngắn (30 phút), hub kiểm vé lúc kết nối. Trả vé còn hạn (>60s),
 * tự refresh (chung mutex với interceptor) nếu vé đã hết/sắp hết hạn. Không có vé (ẩn danh) → null.
 * Refresh lỗi → trả vé hiện có để hành vi không tệ hơn trước.
 */
const ensureFreshAccessToken = async (): Promise<string | null> => {
  const current = LocalStorageService.getItem('accessToken') as string | undefined;
  if (!current) return null;
  let exp: number | undefined;
  try {
    exp = jwtDecode<{ exp?: number }>(current).exp;
  } catch {
    return current;
  }
  if (typeof exp !== 'number' || exp * 1000 - Date.now() > 60_000) return current;
  try {
    return await refreshAccessToken();
  } catch {
    return (LocalStorageService.getItem('accessToken') as string | undefined) || current;
  }
};

const ThrowApiError = (error: ApiError) => {
  if (error.isAxiosError)
    throw error.response?.data.title ?? (error?.response?.data?.message || 1);
  throw error;
};

const setApiAccessToken = (accessToken: string | undefined) => {
  if (accessToken)
    API.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  else delete API.defaults.headers.common.Authorization;
};

export { API, ThrowApiError, setApiAccessToken, ensureFreshAccessToken };
